import React, { useMemo, useState, FC, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { Box, debounce, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  AvatarWithInitials,
  DialogComponent,
  ListboxComponent,
  TextField,
} from 'common/components';
import messages from 'common/components/PersonProfile/messages';
import inputLabels from 'common/messages/inputLabels';
import { INamedEntity } from 'common/interfaces/common';
import { getInitials } from 'helpers/common';
import { useRenderIntlMessage } from 'common/hooks/useRenderIntlMessage';
import { getRequiredErrorMessage } from 'common/utils/validation';
import { formatPersonalCode } from 'common/utils';
import Autocomplete from 'common/components/Autocomplete/Autocomplete';
import { SearchReferralMembersReturn } from 'common/hooks/useSearchReferralMembers';

const validationSchema = yup.object().shape({
  referredBy: yup
    .mixed()
    .notOneOf(['', null], getRequiredErrorMessage)
    .required(getRequiredErrorMessage),
});

interface IFormValues {
  referredBy: Record<string, any>[] | null;
}

interface IPotentialReferralModalProps {
  isOpen: boolean;
  getReferralByValue: SearchReferralMembersReturn;
  onSendReferral: (id: string) => void;
  onClose: () => void;
  personId?: number;
}

const useStyles = makeStyles(() => ({
  optionName: {
    fontSize: '0.9rem',
    fontWeight: 500,
  },
}));

const PotentialReferralsModal: FC<IPotentialReferralModalProps> = ({
  isOpen,
  getReferralByValue,
  onSendReferral,
  onClose,
  personId,
}) => {
  const [isOpenDropdown, setIsOpenDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [referrals, setReferrals] = useState<INamedEntity[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);

  const classes = useStyles();
  const renderIntlMessage = useRenderIntlMessage();

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<IFormValues>({
    defaultValues: {
      referredBy: null,
    },
    resolver: yupResolver(validationSchema) as any, // TODO - PRM-1810 need resolver type
    mode: 'onSubmit',
  });

  const renderUserOption = ({ id, title, imageUrl }: INamedEntity) => {
    const [firstName, lastName] = title.split(/\s(.*)/);

    const initials = getInitials(firstName, lastName);

    return (
      <Box display="flex" key={id}>
        <AvatarWithInitials
          imageUrl={imageUrl}
          width="36px"
          height="36px"
          fontSize="1rem"
          initials={initials}
        />

        <Box display="flex" alignItems="center">
          <Typography className={classes.optionName}>{`${formatPersonalCode(
            Number(id),
          )} - ${title}`}</Typography>
        </Box>
      </Box>
    );
  };

  const loadReferrals = useCallback(
    async (search: string, reset: boolean = false) => {
      if (reset) {
        setPage(0);
        setHasMore(true);
      }
      const currentPage = reset ? 0 : page;
      await getReferralByValue({
        search: search ?? '',
        page: currentPage,
        personId: personId || 0,
      })
        .unwrap()
        .then(response => {
          if (reset) {
            setReferrals(response.data || []);
          } else {
            setReferrals(prev => [...prev, ...response.data]);
          }
          setHasMore(response.data.length === 30);
          setPage(prev => prev + 1);
        });
    },
    [getReferralByValue, page, personId],
  );

  const onChangeSearch = useMemo(() => {
    return debounce((value: string) => {
      setSearchTerm(value);
      loadReferrals(value, true);
    }, 500);
  }, [loadReferrals]);

  const onScroll = (event: React.UIEvent<HTMLUListElement>) => {
    const list = event.currentTarget;
    if (list.scrollTop + list.clientHeight >= list.scrollHeight - 5 && !loadingMore && hasMore) {
      setLoadingMore(true);
      loadReferrals(searchTerm).finally(() => setLoadingMore(false));
    }
  };

  const onSubmit = value => {
    onSendReferral(value.referredBy?.id);
  };

  const ListboxProps = {
    itemSize: 45,
    limitCount: 8,
    onScroll,
  };

  return (
    <DialogComponent
      isOpen={isOpen}
      onSubmit={handleSubmit(onSubmit)}
      onClose={onClose}
      title={<FormattedMessage {...messages.selectReferral} />}
      size="sm"
    >
      <form>
        <Grid item xs={12}>
          <Controller
            control={control}
            name="referredBy"
            defaultValue={'' as any} // TODO - PRM-1810 need type
            render={({ field }) => {
              return (
                <Autocomplete
                  open={isOpenDropdown}
                  onOpen={() => {
                    setIsOpenDropdown(true);
                    if (referrals.length === 0) {
                      loadReferrals('', true);
                    }
                  }}
                  onClose={() => setIsOpenDropdown(false)}
                  onChange={(e, val) => {
                    field.onChange(val);
                  }}
                  value={field.value}
                  getOptionLabel={option => (option ? option.title : '')}
                  renderOption={renderUserOption}
                  onInputChange={(e, val) => {
                    onChangeSearch(val || '');
                  }}
                  onFocus={(e: React.BaseSyntheticEvent) => {
                    onChangeSearch(e.target.value || '');
                  }}
                  onBlur={() => {
                    field.onBlur();
                    setReferrals([]);
                    setHasMore(true);
                    setPage(0);
                  }}
                  ListboxProps={ListboxProps}
                  ListboxComponent={ListboxComponent}
                  options={referrals}
                  loading={loadingMore}
                  renderInput={params => {
                    return (
                      <TextField
                        {...params}
                        label={<FormattedMessage {...inputLabels.referral} />}
                        variant="outlined"
                        fullWidth
                        error={!!errors.referredBy}
                        helperText={renderIntlMessage(errors.referredBy?.message)}
                        InputProps={{
                          ...params.InputProps,
                          autoComplete: 'off',
                          endAdornment: <>{params.InputProps.endAdornment}</>,
                        }}
                      />
                    );
                  }}
                />
              );
            }}
          />
        </Grid>
      </form>
    </DialogComponent>
  );
};

export default PotentialReferralsModal;
